import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { FC, useEffect, useId } from 'react';
import { FormProvider, Path, useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import {
  type Counterparty,
  counterpartyCreateCounterpartyMutation,
  counterpartyGetCounterpartyOptions,
  counterpartyListCounterpartiesQueryKey,
} from '@column/api';
import { Icon } from '@column/column-ui-kit';
import {
  Button,
  Field,
  FieldLabel,
  Input,
  RadioGroup,
  RadioGroupItem,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch,
  fieldGroupVariants,
} from '@column/ui';

import { PageActions } from '~/app/Layout/PageFrameContext';
import { ROUTE } from '~/app/routes';
import { AddressFields, RenderFields, RestrictedBanner } from '~/components';
import { SectionField } from '~/components/RenderFields';
import { RoutingNumberField } from '~/components/RoutingNumberField';
import { useFormErrorHandler } from '~/hooks/useFormErrorHandler';
import { getErrorMessage } from '~/lib/api';
import { COUNTERPARTY_ACCOUNT_TYPE_OPTIONS, coerceCounterpartyAccountType } from '~/lib/counterparties/accountType';
import { useNavigate } from '~/lib/navigation';
import { useNotificationStore } from '~/stores/Notification';
import { useSessionStore } from '~/stores/Session';

interface Params {
  id: string;
}

interface CounterpartyForm {
  id: string;
  description: string;
  routingNumber: string;
  routingNumberType: string;
  accountNumber: string;
  accountType: string;
  wireDrawdownAllowed: boolean;
  wire: {
    beneficiaryName: string;
    beneficiaryEmail: string;
    beneficiaryPhone: string;
    beneficiaryLegalId: string;
    beneficiaryType: string;
    localBankCode: string;
    localAccountNumber: string;
    beneficiaryAddress: {
      line1: string;
      line2: string;
      city: string;
      state: string;
      postalCode: string;
      countryCode: string;
    };
  };
}

type Option = { label: string; value: string };

const ROUTING_NUMBER_TYPE_OPTIONS: Option[] = [
  { label: 'ABA', value: 'aba' },
  { label: 'BIC', value: 'bic' },
];

const BENEFICIARY_TYPE_OPTIONS: Option[] = [
  { label: 'Business', value: 'business' },
  { label: 'Nonprofit', value: 'non_profit' },
  { label: 'Individual', value: 'individual' },
  { label: 'Sole Proprietor', value: 'sole_proprietor' },
];

const toFormValues = (counterparty?: Counterparty): CounterpartyForm => {
  const wire = counterparty?.wire;
  const address = wire?.beneficiary_address;
  return {
    id: counterparty?.id ?? '',
    description: counterparty?.description ?? '',
    routingNumber: counterparty?.routing_number ?? '',
    // The API treats a missing routing number type as ABA.
    routingNumberType: counterparty ? counterparty.routing_number_type : 'aba',
    accountNumber: counterparty?.account_number ?? '',
    accountType: counterparty ? counterparty.account_type : 'checking',
    wireDrawdownAllowed: counterparty?.wire_drawdown_allowed ?? false,
    wire: {
      beneficiaryName: wire?.beneficiary_name ?? '',
      beneficiaryEmail: wire?.beneficiary_email ?? '',
      beneficiaryPhone: wire?.beneficiary_phone ?? '',
      beneficiaryLegalId: wire?.beneficiary_legal_id ?? '',
      beneficiaryType: wire?.beneficiary_type ?? '',
      localBankCode: wire?.local_bank_code ?? '',
      localAccountNumber: wire?.local_account_number ?? '',
      beneficiaryAddress: {
        line1: address?.line_1 ?? '',
        line2: address?.line_2 ?? '',
        city: address?.city ?? '',
        state: address?.state ?? '',
        postalCode: address?.postal_code ?? '',
        countryCode: address?.country_code ?? '',
      },
    },
  };
};

// Two-option fields, shown as a split radio pair.
const SplitRadioGroup: FC<{
  options: Option[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}> = ({ options, value, onChange, disabled }) => {
  const groupId = useId();
  return (
    <RadioGroup
      className={fieldGroupVariants({ variant: 'outline', columns: 2 })}
      value={value}
      onValueChange={onChange}
      disabled={disabled}
    >
      {options.map((option) => (
        <Field key={option.value} orientation="horizontal" data-disabled={disabled || undefined}>
          <RadioGroupItem value={option.value} id={`${groupId}-${option.value}`} disabled={disabled} />
          <FieldLabel htmlFor={`${groupId}-${option.value}`}>{option.label}</FieldLabel>
        </Field>
      ))}
    </RadioGroup>
  );
};

export const PageCounterpartiesEdit: FC = () => {
  const { currentPermission } = useSessionStore();
  const { addSuccessNotification, addDangerNotification } = useNotificationStore();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { id } = useParams<keyof Params>() as Params;
  const isReadOnly = Boolean(id);
  const hasWritePermission = currentPermission?.counterparties === 'write';
  // Read-only views skip validation, which also hides the required markers.
  const requiredRule = isReadOnly ? undefined : { required: true };

  const counterpartyQuery = useQuery({
    ...counterpartyGetCounterpartyOptions({ path: { counterparty_id: id } }),
    enabled: isReadOnly,
  });
  const createCounterparty = useMutation(counterpartyCreateCounterpartyMutation());

  const formMethods = useForm<CounterpartyForm>({ defaultValues: toFormValues() });
  const { handleSubmit, reset } = formMethods;
  const { handleSubmitErrors } = useFormErrorHandler();

  useEffect(() => {
    if (counterpartyQuery.data) reset(toFormValues(counterpartyQuery.data));
  }, [counterpartyQuery.data, reset]);

  useEffect(() => {
    if (!counterpartyQuery.error) return;
    navigate(ROUTE.COUNTERPARTIES);
    addDangerNotification({ content: getErrorMessage(counterpartyQuery.error) });
  }, [counterpartyQuery.error, navigate, addDangerNotification]);

  const onSubmit = (data: CounterpartyForm) => {
    if (isReadOnly) return;

    const { wire } = data;
    const address = wire.beneficiaryAddress;
    createCounterparty.mutate(
      {
        body: {
          description: data.description,
          routing_number: data.routingNumber,
          routing_number_type: data.routingNumberType,
          account_number: data.accountNumber,
          account_type: coerceCounterpartyAccountType(data.accountType) ?? 'checking',
          wire_drawdown_allowed: data.wireDrawdownAllowed,
          wire: {
            beneficiary_name: wire.beneficiaryName,
            beneficiary_email: wire.beneficiaryEmail,
            beneficiary_phone: wire.beneficiaryPhone,
            beneficiary_legal_id: wire.beneficiaryLegalId,
            beneficiary_type: wire.beneficiaryType,
            local_bank_code: wire.localBankCode,
            local_account_number: wire.localAccountNumber,
            beneficiary_address: {
              line_1: address.line1,
              line_2: address.line2,
              city: address.city,
              state: address.state,
              postal_code: address.postalCode,
              country_code: address.countryCode,
            },
          },
        },
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries({ queryKey: counterpartyListCounterpartiesQueryKey() });
          navigate(ROUTE.COUNTERPARTIES);
          addSuccessNotification({ content: 'Counterparty added' });
        },
        onError: (error) => {
          addDangerNotification({ content: getErrorMessage(error, 'Failed to create counterparty') });
        },
      }
    );
  };

  const textField = (
    fieldId: Path<CounterpartyForm>,
    label: string,
    placeholder: string,
    required?: boolean
  ): SectionField<CounterpartyForm, undefined> => ({
    id: fieldId,
    label,
    rules: required ? requiredRule : undefined,
    children: ({ value, onChange, onBlur, ref }, { error }) =>
      isReadOnly ? (
        <Input variant="copy" readOnly showCopyButton value={String(value ?? '')} />
      ) : (
        <Input
          ref={ref}
          value={String(value ?? '')}
          onChange={onChange}
          onBlur={onBlur}
          placeholder={placeholder}
          aria-invalid={!!error}
        />
      ),
  });

  const selectField = (
    fieldId: Path<CounterpartyForm>,
    label: string,
    options: Option[],
    required?: boolean
  ): SectionField<CounterpartyForm, undefined> => ({
    id: fieldId,
    label,
    rules: required ? requiredRule : undefined,
    children: ({ value, onChange }, { error }) => (
      <Select value={value ? String(value) : undefined} onValueChange={onChange} disabled={isReadOnly}>
        <SelectTrigger size="lg" className="w-full" aria-invalid={!!error}>
          <SelectValue placeholder="Select type" />
        </SelectTrigger>
        <SelectContent>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
    ),
  });

  const cancelButton = (
    <Button onClick={() => navigate(-1)} variant="secondary" size="sm" type="button">
      {isReadOnly ? 'Back' : 'Cancel'}
    </Button>
  );
  const createButton = (
    <Button
      onClick={handleSubmit(onSubmit, handleSubmitErrors)}
      disabled={!hasWritePermission || createCounterparty.isPending}
      size="sm"
      type="button"
    >
      <Icon.Plus className="[--icon-size-x:16px] [--icon-size-y:16px]" />
      {createCounterparty.isPending ? 'Creating…' : 'Create Counterparty'}
    </Button>
  );

  return (
    <>
      {!isReadOnly && (
        <PageActions>
          {cancelButton}
          {createButton}
        </PageActions>
      )}

      {!hasWritePermission && !isReadOnly && (
        <div className="px-(--page-margin) pt-6">
          <RestrictedBanner />
        </div>
      )}

      <FormProvider {...formMethods}>
        <form autoComplete="off" onSubmit={handleSubmit(onSubmit, handleSubmitErrors)}>
          {RenderFields<CounterpartyForm>({
            sections: [
              {
                headline: 'Counterparty Information',
                fields: [
                  {
                    id: 'id',
                    label: 'ID',
                    hide: !isReadOnly,
                    children: ({ value }) => (
                      <Input
                        variant="copy"
                        readOnly
                        showCopyButton
                        className="[&_input]:font-mono"
                        value={String(value ?? '')}
                      />
                    ),
                  },
                  textField('description', 'Description', 'Description'),
                  {
                    id: 'routingNumber',
                    label: 'Routing Number',
                    rules: requiredRule,
                    children: ({ value, onChange, onBlur }, { error }) =>
                      isReadOnly ? (
                        <Input variant="copy" readOnly showCopyButton value={String(value ?? '')} />
                      ) : (
                        <RoutingNumberField
                          value={String(value ?? '')}
                          onChange={onChange}
                          onBlur={onBlur}
                          invalid={!!error}
                        />
                      ),
                  },
                  {
                    id: 'routingNumberType',
                    label: 'Routing Number Type',
                    children: ({ value, onChange }) => (
                      <SplitRadioGroup
                        options={ROUTING_NUMBER_TYPE_OPTIONS}
                        value={String(value ?? '')}
                        onChange={onChange}
                        disabled={isReadOnly}
                      />
                    ),
                  },
                  textField('accountNumber', 'Account Number', 'Account number', true),
                  {
                    id: 'accountType',
                    label: 'Account Type',
                    children: ({ value, onChange }) => (
                      <SplitRadioGroup
                        options={COUNTERPARTY_ACCOUNT_TYPE_OPTIONS}
                        value={String(value ?? '')}
                        onChange={onChange}
                        disabled={isReadOnly}
                      />
                    ),
                  },
                ],
              },
              {
                headline: 'Wire',
                fields: [
                  textField('wire.beneficiaryName', 'Beneficiary Name', 'Beneficiary name', true),
                  {
                    id: 'wireDrawdownAllowed',
                    label: 'Wire Drawdown Allowed',
                    children: ({ value, onChange }) => (
                      <Switch
                        className="@tablet/form-field:mt-3"
                        aria-label="Wire Drawdown Allowed"
                        checked={Boolean(value)}
                        onCheckedChange={onChange}
                        disabled={isReadOnly}
                      />
                    ),
                  },
                  textField('wire.beneficiaryEmail', 'Beneficiary Email', 'Beneficiary email'),
                  textField('wire.beneficiaryPhone', 'Beneficiary Phone', 'Beneficiary phone'),
                  textField('wire.beneficiaryLegalId', 'Beneficiary Legal ID', 'Beneficiary legal ID'),
                  selectField('wire.beneficiaryType', 'Beneficiary Type', BENEFICIARY_TYPE_OPTIONS, true),
                  textField('wire.localBankCode', 'Local Bank Code', 'Local bank code'),
                  textField('wire.localAccountNumber', 'Local Account Number', 'Local account number'),
                ],
              },
            ],
          })}
          <AddressFields name="wire.beneficiaryAddress" heading="Beneficiary Address" disabled={isReadOnly} />
          <div className="flex items-start justify-between px-(--page-margin) py-6">
            {cancelButton}
            {!isReadOnly && createButton}
          </div>
        </form>
      </FormProvider>
    </>
  );
};
